import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Fab from '@material-ui/core/Fab';
import Typography from '@material-ui/core/Typography';

import AddIcon from '@material-ui/icons/Add';

import FeedPostItem from 'app/components/feed/FeedPostItem';
import FeedArticleItem from 'app/components/feed/FeedArticleItem';
import FeedEventItem from 'app/components/feed/FeedEventItem';
import FeedElectionItem from 'app/components/feed/FeedElectionItem';
import EditPostDialog from 'app/components/feed/EditPostDialog';
import DeletePostDialog from 'app/components/feed/DeletePostDialog';

import useApi from 'app/context/useApi';
import FeedItemLoading from './FeedItemLoading';

const useStyles = makeStyles(theme => ({
    endOfFeed: {
        position: 'relative',
        paddingTop: theme.spacing(9),
        paddingBottom: theme.spacing(9),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    extendedIcon: {
        marginRight: theme.spacing(1),

        [theme.breakpoints.down('sm')]: {
            marginRight: 0,
        }
    },
}));

export default function FeedContainer(props) {

    const classes = useStyles();
    const api = useApi();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ feed, setFeed ] = useState([]);
    const [ topic, setTopic ] = useState('all');
    const [ groupId, setGroupId ] = useState(0);
    const [ groups, setGroups ] = useState([]);

    const [ editingPost, setEditingPost ] = useState(null);
    const [ editPostDialogOpen, setEditPostDialogOpen ] = useState(false);

    const [ deletingPost, setDeletingPost ] = useState(null);
    const [ deletePostDialogOpen, setDeletePostDialogOpen ] = useState(false);

    const [ filterGroupDialogOpen, setFilterGroupsDialogOpen ] = useState(false);
    const [ filterTopicsDialogOpen, setFilterTopicsDialogOpen ] = useState(false);

    const [ feedLoading, setFeedLoading ] = useState(true);
    const [ groupsLoading, setGroupsLoading ] = useState(false);

    // Getter Functions ------------------------------------------

    const getFeed = () => {

        setFeedLoading(true);

        api.get(`/feed/${groupId}/${topic}`)
            .then(res => setFeed(res.feed))
            .finally(() => setFeedLoading(false));
    }

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/group').then(res => setGroups(res.groups)).finally(() => setGroupsLoading(false));
    };

    useEffect(() => {
        getGroups();
    }, []);

    useEffect(() => {
        getFeed();
    }, [groupId, topic]);

    // Edit Post Dialog Functions ------------------------------------------

    const openEditPostDialog = (post) => {
        setEditPostDialogOpen(true);
        setEditingPost(post);
    }

    const closeEditPostDialog = () => {
        setEditPostDialogOpen(false);
    }

    const closeEditPostDialogAndRefresh = () => {
        closeEditPostDialog();
        getFeed();
    }

    const editPostDialogClosed = () => {
        setEditingPost(null);
    }

    // Delete Post Dialog Functions ------------------------------------------

    const openDeletePostDialog = (post) => {
        setDeletePostDialogOpen(true);
        setDeletingPost(post);
    }

    const closeDeletePostDialog = () => {
        setDeletePostDialogOpen(false);
    }

    const closeDeletePostDialogAndRefresh = () => {
        closeDeletePostDialog();
        getFeed();
    }

    const deletePostDialogClosed = () => {
        setDeletingPost(null);
    }

    // Filter Topic Dialog Functions ------------------------------------------

    const openFilterTopicsDialog = () => {
        setFilterTopicsDialogOpen(true);
    }

    const closeFilterTopicsDialog = () => {
        setFilterTopicsDialogOpen(false);
    }

    const handleTopic = newTopic => {
        setTopic(newTopic);
        closeFilterTopicsDialog();
    }

    // Filter Groups Dialog Functions ------------------------------------------

    const openFilterGroupsDialog = () => {
        setFilterGroupsDialogOpen(true);
    }

    const closeFilterGroupsDialog = () => {
        setFilterGroupsDialogOpen(false);
    }

    const handleGroupId = newGroupId => {
        setGroupId(newGroupId);
        closeFilterGroupsDialog();
    }

    const getTopicsButtonText = useMemo(() => {
        if (topic === 'all') {
            return 'All Topics'
        } else {
            return 'Viewing ' + topic;
        }
    }, [topic]);

    const getGroupsButtonText = useMemo(() => {
        if (groupId === 0) {
            return 'All Groups'
        }

        const selectedGroup = groups.find(group => group.id === groupId);
        return 'Viewing ' + selectedGroup.title;
    }, [groupId]);

    const getFeedItem = (item, key) => {
        switch (item.type) {
            case 'post':
                return (
                    <FeedPostItem
                        key={item.type + '-' + item.id}
                        post={item}
                        onEdit={() => openEditPostDialog(item)}
                        onDelete={() => openDeletePostDialog(item)}
                    />
                )
            case 'article':
                return (
                    <FeedArticleItem
                        key={item.type + '-' + item.id}
                        article={item}
                    />
                )
            case 'event':
                return (
                    <FeedEventItem
                        key={item.type + '-' + item.id}
                        event={item}
                    />
                )
            case 'election':
                return (
                    <FeedElectionItem
                        key={item.type + '-' + item.id}
                        election={item}
                    />
                )
        }
    }

    return (
        <div className={classes.feed}>
            <Grid container spacing={3} justify="space-between" alignItems="center">
                {(props.app.user.membership_status?.is_admin) &&
                    <Grid item>
                        <Fab
                            variant={isMobile ? null : "extended"}
                            onClick={() => openEditPostDialog(null)}
                            color="secondary"
                            aria-label="New Post"
                            >
                            <AddIcon className={classes.extendedIcon} />
                            {isMobile ? null :  "New Post"}
                        </Fab>
                    </Grid>
                }
                <Grid item>
                    <ButtonGroup variant="contained" color="secondary" aria-label="Feed Filters" size={isMobile ? 'small' : 'medium'}>
                        <Button onClick={openFilterTopicsDialog}>
                            {getTopicsButtonText}
                        </Button>
                        <Button onClick={openFilterGroupsDialog}>
                            {getGroupsButtonText}
                        </Button>
                    </ButtonGroup>
                </Grid>
            </Grid>
            <div className={classes.feedContainer}>
                {feedLoading ? (
                    <>
                        <FeedItemLoading />
                        <FeedItemLoading />
                        <FeedItemLoading />
                        <FeedItemLoading />
                        <FeedItemLoading />
                    </>
                ) : feed.map((item, key) => getFeedItem(item, key))}
                {(! feedLoading) &&
                    <div className={classes.endOfFeed} id="end-of-feed">
                        <Typography variant="h5" color="textSecondary" align="center">
                            You're all caught up!<br/>Come back soon to catch up on your feed.
                        </Typography>
                    </div>
                }
            </div>
            <EditPostDialog
                post={editingPost}
                initGroups={groups}
                initGroupsLoading={groupsLoading}
                open={editPostDialogOpen}
                onClose={closeEditPostDialog}
                onPublish={closeEditPostDialogAndRefresh}
                onClosed={editPostDialogClosed}
            />
            <DeletePostDialog
                post={deletingPost}
                open={deletePostDialogOpen}
                onClose={closeDeletePostDialog}
                onDelete={closeDeletePostDialogAndRefresh}
                onClosed={deletePostDialogClosed}
            />
            <Dialog
                aria-labelledby="filter-topic-dialog-title"
                open={filterTopicsDialogOpen}
                fullWidth
                maxWidth="xs"
                onClose={closeFilterTopicsDialog}
                >
                <DialogTitle id="filter-topic-dialog-title">
                    Filter Feed by Topic
                </DialogTitle>
                <DialogContent>
                    <List dense>
                        <ListItem
                            button
                            onClick={() => handleTopic('all')}
                            selected={topic === 'all'}
                            >
                            <ListItemText primary='All Topics' />
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => handleTopic('posts')}
                            selected={topic === 'posts'}
                            >
                            <ListItemText primary='Posts' />
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => handleTopic('articles')}
                            selected={topic === 'articles'}
                            >
                            <ListItemText primary='Articles' />
                        </ListItem>
                    </List>
                </DialogContent>
            </Dialog>
            <Dialog
                aria-labelledby="filter-group-dialog-title"
                open={filterGroupDialogOpen}
                fullWidth
                maxWidth="xs"
                onClose={closeFilterGroupsDialog}
                >
                <DialogTitle id="filter-group-dialog-title">
                    Filter Feed by Group
                </DialogTitle>
                <DialogContent>
                    <List dense>
                        <ListItem
                            button
                            onClick={() => handleGroupId(0)}
                            selected={groupId === 0}
                            >
                            <ListItemText primary='All Groups' />
                        </ListItem>
                        {groups.map((group) => (
                            <ListItem
                                key={group.id}
                                button
                                onClick={() => handleGroupId(group.id)}
                                selected={groupId === group.id}
                                >
                                <ListItemText primary={group.title} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>
        </div>
    )
}
